import React, { useEffect, useState } from "react";
import { User, FlaskConical, ClipboardList, Stethoscope, CalendarClock, UserPlus, XCircle, ArrowLeft } from "lucide-react";
import { FaSpinner } from "react-icons/fa";
import toast from "react-hot-toast";
import "../../Complaint.css";
import "./HVTRegistration.css";
import { store } from "../../Store/store";
import * as HVTRegistrationService from "../../Services/HVTRegistration/HVTRegistrationService";
import { formatDateTimeAMPM, formatTimeAMPM } from "./HVTCommonHelpers";

const NotProvided = () => <span className="text-muted">Not Provided</span>;

const STATUS_STYLES = {
    Registered: "success",
    "Phlebo Reached": "success",
    "Samples Collected": "success",
    "Samples Delivered": "success",
    "Wait List": "warn",
    Cancelled: "danger",
    Aborted: "danger",
};

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "timeline", label: "Timeline" },
    { key: "tests", label: "Tests & Payment" },
];

const HVTOrderStatusModal = ({
    bookingId,
    preloadedBooking,
    preloadedPrimaryDetails,
    item,
    onClose,
    onReschedule,
    onAddPatient,
    onCancel,
    isRescheduleLoading,
    isAddPatientLoading,
    hideActions = false,
}) => {
    const [loading, setLoading] = useState(!preloadedBooking);
    const [booking, setBooking] = useState(preloadedBooking || null);
    const [primaryRegDetails, setPrimaryRegDetails] = useState(preloadedPrimaryDetails || null);
    const [activeTab, setActiveTab] = useState("overview");
    const [parentSnapshot, setParentSnapshot] = useState(null);

    useEffect(() => {
        if (preloadedBooking) return;
        HVTRegistrationService.GetBookingDetailsByBookingId(bookingId)
            .then(({ data }) => {
                const details = data?.Data?.BookingDetails?.[0];
                if (!data?.IsSuccess || !details) {
                    // toast.error(data?.ResponseMessage || "Unable to load registration details.");
                    toast.error("HVT Error: Unable to load registration details.");
                    return;
                }
                setBooking(details);
                setPrimaryRegDetails(data?.Data?.PrimaryRegistrationDetails || null);
            })
            .catch(() => toast.error("HVT Error: Something went wrong while loading registration details."))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingId]);

    const handleChildBookingClick = (childBookingId) => {
        if (!childBookingId) return;
        setParentSnapshot({ booking, primaryRegDetails });
        setLoading(true);
        HVTRegistrationService.GetBookingDetailsByBookingId(childBookingId)
            .then(({ data }) => {
                const details = data?.Data?.BookingDetails?.[0];
                if (!data?.IsSuccess || !details) {
                    toast.error("HVT Error: Unable to load registration details.");
                    setParentSnapshot(null);
                    return;
                }
                setBooking(details);
                setPrimaryRegDetails(data?.Data?.PrimaryRegistrationDetails || null);
                setActiveTab("overview");
            })
            .catch(() => {
                toast.error("HVT Error: Something went wrong while loading registration details.");
                setParentSnapshot(null);
            })
            .finally(() => setLoading(false));
    };

    const handleBackToPrimary = () => {
        if (!parentSnapshot) return;
        setBooking(parentSnapshot.booking);
        setPrimaryRegDetails(parentSnapshot.primaryRegDetails);
        setParentSnapshot(null);
        setActiveTab("overview");
    };

    const tests = booking?.TestDetails || [];
    const payments = booking?.PaymentDetails || [];
    const phleboDetails = booking?.PhleboDetails || [];
    const childBookings = booking?.ChildBooking || [];

    const isCreditClient = store.getState()?.Partner_Account_Details?.MHL_Payment_Type__c === "Credit";
    const isCancelledVisit = booking?.VisitStatus === "Cancelled";
    const hasPaymentDetails = payments.length > 0;
    const totalReceivable = booking?.TotalReceivables?.[0];
    const totalAmount = totalReceivable?.NetAmount;
    const paidDate = payments?.[0]?.PaymentDate || null;

    const patientName = [booking?.PatientFirstName, booking?.PatientMiddleName, booking?.PatientLastName]
        .filter(Boolean).join(" ");

    const isViewingChild = !!parentSnapshot;
    const showPrimaryTab = !!primaryRegDetails && booking?.GroupIdType !== "Primary";
    const tabs = showPrimaryTab ? [...TABS, { key: "primary", label: "Primary Booking" }] : TABS;
    const isRegistered = booking?.VisitStatus === "Registered";
    const reschedulable = !isViewingChild && isRegistered && !!item?.IsPrimary;
    const isGroupFull = (1 + childBookings.length) >= 3;
    const addPatientable = !isViewingChild && isRegistered && !!item?.IsPrimary && !isGroupFull;
    const cancellable = !isViewingChild && isRegistered;

    const timelineSteps = booking ? [
        { label: "Registered", time: booking.BookingDateTime },
        { label: "Visit slot", time: booking.VisitDateTime },
        { label: "Phlebo Started", time: booking.PhleboStarted },
        { label: "Reached", time: booking.ReachedDateTime },
        { label: "Collected", time: booking.CollectedDateTime, timeOnly: true },
        { label: "Delivered", time: booking.DeliveryDateTime, timeOnly: true },
    ] : [];

    return (
        <div className="modal-backdrop">
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-xl">
                    <div className="modal-content" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                        <div className="modal-header custom-card-heading text-white p-2" style={{ flexShrink: 0 }}>
                            <h5 className="modal-title text-white px-2 d-flex align-items-center gap-2">
                                {isViewingChild && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light d-flex align-items-center justify-content-center p-0"
                                        style={{ width: 26, height: 26 }}
                                        onClick={handleBackToPrimary}
                                        title="Back to primary booking"
                                    >
                                        <ArrowLeft size={14} />
                                    </button>
                                )}
                                Home Visit Details {booking?.BookingId ? `(${booking.BookingId})` : ""}
                            </h5>
                            <button type="button" className="btn-close btn-close-white mr-1" onClick={onClose}></button>
                        </div>

                        {loading ? (
                            <div className="text-center py-4"><FaSpinner className="fa-spin" /></div>
                        ) : !booking ? (
                            <div className="text-center text-muted py-4">No details found for this registration.</div>
                        ) : (
                            <>
                                <div className="d-flex gap-2 justify-content-end px-3 py-2" style={{ borderBottom: "1px solid #e5e7eb", flexShrink: 0 }} hidden={hideActions}>
                                    {item?.IsPrimary && !isViewingChild && (
                                        <button
                                            type="button"
                                            className="btn btn-sm custom-sumbitbtn d-flex align-items-center gap-1"
                                            disabled={!reschedulable || isRescheduleLoading}
                                            style={!reschedulable ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "auto" } : undefined}
                                            title={
                                                reschedulable
                                                    ? "Reschedule"
                                                    : !isRegistered
                                                        ? "Available only when Visit Status is Registered"
                                                        : "This booking has been cancelled"
                                            }
                                            onClick={() => onReschedule?.(item)}
                                        >
                                            {isRescheduleLoading ? <FaSpinner className="fa-spin" size={14} /> : <CalendarClock size={14} />} Reschedule
                                        </button>
                                    )}
                                    {/* Add Patient — commented out for now, functionality to be revisited
                                    {item?.IsPrimary && !isViewingChild && (
                                        <button
                                            type="button"
                                            className="btn btn-sm custom-sumbitbtn d-flex align-items-center gap-1"
                                            disabled={!addPatientable || isAddPatientLoading}
                                            style={!addPatientable ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "auto" } : undefined}
                                            title={
                                                addPatientable
                                                    ? "Add Patient"
                                                    : isGroupFull
                                                        ? "This group booking already has the maximum of 3 patients"
                                                        : !isRegistered
                                                            ? "Available only when Visit Status is Registered"
                                                            : "This booking has been cancelled"
                                            }
                                            onClick={() => onAddPatient?.(item)}
                                        >
                                            {isAddPatientLoading ? <FaSpinner className="fa-spin" size={14} /> : <UserPlus size={14} />} Add Patient
                                        </button>
                                    )}
                                    */}
                                    {!isViewingChild && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                                            disabled={!cancellable}
                                            style={!cancellable ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "auto" } : undefined}
                                            title={
                                                cancellable
                                                    ? "Cancel"
                                                    : !isRegistered
                                                        ? "Available only when Visit Status is Registered"
                                                        : "Already cancelled"
                                            }
                                            onClick={() => onCancel?.(item)}
                                        >
                                            <XCircle size={14} /> Cancel
                                        </button>
                                    )}
                                </div>

                                <div className="hvt-order-tabbar">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            className={activeTab === tab.key ? "active" : ""}
                                            onClick={() => setActiveTab(tab.key)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="modal-body" style={{ overflowY: "auto" }}>
                                    {activeTab === "overview" && (
                                        <div className="hvt-order-tabpanel">
                                            <div className="hvt-order-grid3">
                                                <div className="hvt-order-field">
                                                    <div className="k">Patient Name</div>
                                                    <div className="v">{patientName || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Age</div>
                                                    <div className="v">{booking.Age || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Visit Status</div>
                                                    <div className="v">
                                                        <span className={`hvt-order-status hvt-order-status-${STATUS_STYLES[booking.VisitStatus] || "neutral"}`}>
                                                            {booking.VisitStatus || "Unknown"}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Visit Date & Time</div>
                                                    <div className="v">{booking.VisitDateTime ? formatDateTimeAMPM(booking.VisitDateTime) : <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Booking Type</div>
                                                    <div className="v">{booking.BookingIdType || "Individual"}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Call Source</div>
                                                    <div className="v">{booking.CallSource || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Mobile</div>
                                                    <div className="v">{booking.MobileNumber || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Alternative Contact</div>
                                                    <div className="v">{booking.AlternativeContactNumber || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Email</div>
                                                    <div className="v">{booking.EmailId || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field full">
                                                    <div className="k">Address</div>
                                                    <div className="v">{booking.AddressLine || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Group ID</div>
                                                    <div className="v">{booking.GroupId || "NA"} {booking.GroupIdType ? `(${booking.GroupIdType})` : ""}</div>
                                                </div>
                                                {/* <div className="hvt-order-field">
                                                    <div className="k">Prandial ID</div>
                                                    <div className="v">{booking.PrandialId || "NA"} {booking.PrandialIdType ? `(${booking.PrandialIdType})` : ""}</div>
                                                </div> */}
                                                <div className="hvt-order-field">
                                                    <div className="k">VID</div>
                                                    <div className="v">{booking.Vid || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Client Code</div>
                                                    <div className="v">{booking.ClientCode || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Client Name</div>
                                                    <div className="v">{booking.ClientName || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Client Type</div>
                                                    <div className="v">{booking.ClientType || <NotProvided />}</div>
                                                </div>
                                            </div>

                                            {childBookings.length > 0 && (
                                                <div style={{ marginTop: 18 }}>
                                                    <div className="hvt-quick-section-title" style={{ marginBottom: 8 }}>
                                                        <ClipboardList size={13} /> Child Bookings ({childBookings.length})
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {childBookings.map((child, idx) => {
                                                            const childId = typeof child === "string" ? child : child.BookingId;
                                                            return (
                                                                <span
                                                                    key={idx}
                                                                    className="hvt-test-pill"
                                                                    role={childId ? "button" : undefined}
                                                                    style={childId ? { cursor: "pointer" } : undefined}
                                                                    title={childId ? "View this booking" : undefined}
                                                                    onClick={() => handleChildBookingClick(childId)}
                                                                >
                                                                    {childId || JSON.stringify(child)}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === "timeline" && (
                                        <div className="hvt-order-tabpanel">
                                            <div className="hvt-order-grid3">
                                                {timelineSteps.map((step) => (
                                                    <div key={step.label} className="hvt-order-field">
                                                        <div className="k">{step.label}</div>
                                                        <div className="v">{step.time ? (step.timeOnly ? formatTimeAMPM(step.time) : formatDateTimeAMPM(step.time)) : <NotProvided />}</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="hvt-quick-section-title" style={{ marginTop: 18, marginBottom: 8 }}>
                                                <Stethoscope size={13} /> Assigned Phlebo
                                            </div>
                                            {phleboDetails.length > 0 ? (
                                                phleboDetails.map((p, idx) => (
                                                    <div key={idx} className="hvt-order-grid3" style={{ marginBottom: idx !== phleboDetails.length - 1 ? 12 : 0 }}>
                                                        <div className="hvt-order-field">
                                                            <div className="k">Name</div>
                                                            <div className="v">{p.PhleboName || <NotProvided />}</div>
                                                        </div>
                                                        <div className="hvt-order-field">
                                                            <div className="k">Visit Type</div>
                                                            <div className="v">{p.PhleboVisitType || <NotProvided />}</div>
                                                        </div>
                                                        <div className="hvt-order-field">
                                                            <div className="k">Star Rating</div>
                                                            <div className="v">{p.PhleboStarRating ?? 0} / 5</div>
                                                        </div>
                                                        <div className="hvt-order-field">
                                                            <div className="k">Visit Count</div>
                                                            <div className="v">{p.PhleboVisitCount ?? <NotProvided />}</div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <small className="text-muted">No phlebo assigned yet</small>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === "tests" && (
                                        <div className="hvt-order-tabpanel">
                                            <div className="hvt-quick-section-title" style={{ marginBottom: 10 }}>
                                                <FlaskConical size={13} /> Tests ({tests.length})
                                            </div>
                                            {tests.length > 0 ? (
                                                tests.map((t) => (
                                                    <div key={t.TestCode} className="hvt-review-testline">
                                                        <span>{t.TestName} ({t.TestCode})</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <small className="text-muted">No tests found</small>
                                            )}

                                            <div className="hvt-quick-section-title" style={{ marginTop: 18, marginBottom: 8 }}>
                                                <User size={13} /> Payment
                                            </div>
                                            <div className="hvt-order-grid3">
                                                <div className="hvt-order-field">
                                                    <div className="k">Payment Mode</div>
                                                    <div className="v">{isCreditClient ? "Credit" : "Cash"}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Total Amount</div>
                                                    <div className="v">{totalAmount != null ? `₹${totalAmount}` : <NotProvided />}</div>
                                                </div>
                                                {!isCreditClient && !isCancelledVisit && (
                                                    <div className="hvt-order-field">
                                                        <div className="k">Payment Status</div>
                                                        <div className="v">
                                                            <span className={hasPaymentDetails ? "hvt-confirmed-pill" : "hvt-pending-pill"}>
                                                                {hasPaymentDetails ? "Paid" : "Pending"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {!isCreditClient && !isCancelledVisit && (
                                                    <div className="hvt-order-field">
                                                        <div className="k">Paid Date</div>
                                                        <div className="v">{paidDate ? formatDateTimeAMPM(paidDate) : <NotProvided />}</div>
                                                    </div>
                                                )}
                                            </div>
                                            {!isCreditClient && !isCancelledVisit && !hasPaymentDetails && totalAmount != null && (
                                                <div className="hvt-payable-note">
                                                    Payable Amount to Phlebotomist: <strong>&#8377;{totalAmount}</strong>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === "primary" && showPrimaryTab && (
                                        <div className="hvt-order-tabpanel">
                                            <div className="hvt-order-grid3">
                                                <div className="hvt-order-field">
                                                    <div className="k">Patient Name</div>
                                                    <div className="v">{primaryRegDetails.Registration?.Name || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Booking ID</div>
                                                    <div className="v">{primaryRegDetails.Registration?.BookingId || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Mobile</div>
                                                    <div className="v">{primaryRegDetails.Registration?.Mobile || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Email</div>
                                                    <div className="v">{primaryRegDetails.Registration?.EmailId || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Gender</div>
                                                    <div className="v">{primaryRegDetails.Registration?.GenderId || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Booking Date & Time</div>
                                                    <div className="v">{primaryRegDetails.Registration?.BookingDate ? formatDateTimeAMPM(primaryRegDetails.Registration.BookingDate) : <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field">
                                                    <div className="k">Location</div>
                                                    <div className="v">{primaryRegDetails.Registration?.LocationName || <NotProvided />}</div>
                                                </div>
                                                <div className="hvt-order-field full">
                                                    <div className="k">Address</div>
                                                    <div className="v">{primaryRegDetails.Registration?.AddressLine || <NotProvided />}</div>
                                                </div>
                                            </div>

                                            <div className="hvt-quick-section-title" style={{ marginTop: 18, marginBottom: 10 }}>
                                                <FlaskConical size={13} /> Tests ({(primaryRegDetails.HomeCollection || []).length})
                                            </div>
                                            {(primaryRegDetails.HomeCollection || []).length > 0 ? (
                                                primaryRegDetails.HomeCollection.map((t) => (
                                                    <div key={t.HomeCollectionId || t.TestCode} className="hvt-review-testline">
                                                        <span>{t.TestName} ({t.TestCode})</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <small className="text-muted">No tests found</small>
                                            )}

                                            <div className="hvt-quick-section-title" style={{ marginTop: 18, marginBottom: 8 }}>
                                                <User size={13} /> Payment
                                            </div>
                                            {primaryRegDetails.PaymentDetails ? (
                                                <div className="hvt-order-grid3">
                                                    <div className="hvt-order-field">
                                                        <div className="k">Mode</div>
                                                        <div className="v">{primaryRegDetails.PaymentDetails.PaymentMode || <NotProvided />}</div>
                                                    </div>
                                                    <div className="hvt-order-field">
                                                        <div className="k">Paid</div>
                                                        <div className="v">{primaryRegDetails.PaymentDetails.TransactionDate ? formatDateTimeAMPM(primaryRegDetails.PaymentDetails.TransactionDate) : <NotProvided />}</div>
                                                    </div>
                                                    <div className="hvt-order-field full">
                                                        <div className="k">Order ID</div>
                                                        <div className="v" style={{ wordBreak: "break-all" }}>{primaryRegDetails.PaymentDetails.OrderId || <NotProvided />}</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <small className="text-muted">No payment recorded</small>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HVTOrderStatusModal;
